import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Landmark, Search, Save, Loader2, CheckCircle2 } from "lucide-react";

const YEARS = ["2018", "2022"];
const FIELDS = [["biz", "Bize"], ["karsi", "Karşı"], ["gelmedi", "Gelmeyen"]];

function share(t) {
  const voted = (t.biz || 0) + (t.karsi || 0);
  return voted ? Math.round((t.biz / voted) * 100) : 0;
}
function hasData(e) {
  return YEARS.some((y) => (e[y]?.biz || 0) + (e[y]?.karsi || 0) + (e[y]?.gelmedi || 0) > 0);
}

export default function ElectionResults() {
  const erT = useTableSort([]);
  const [committees, setCommittees] = useState(null);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | empty | filled
  const [savingNo, setSavingNo] = useState(null);

  const load = async () => {
    const { data } = await api.get("/committees");
    setCommittees(data);
    const init = {};
    for (const c of data) {
      const et = c.election_totals || {};
      init[c.committee_no] = {
        "2018": { biz: et["2018"]?.biz ?? "", karsi: et["2018"]?.karsi ?? "", gelmedi: et["2018"]?.gelmedi ?? "" },
        "2022": { biz: et["2022"]?.biz ?? "", karsi: et["2022"]?.karsi ?? "", gelmedi: et["2022"]?.gelmedi ?? "" },
      };
    }
    setEdits(init);
  };
  useEffect(() => { load(); }, []);

  const setVal = (no, year, field, v) => {
    const clean = v.replace(/[^\d]/g, "");
    setEdits((p) => ({ ...p, [no]: { ...p[no], [year]: { ...p[no][year], [field]: clean } } }));
  };

  const numify = (e) => {
    const out = {};
    for (const y of YEARS) {
      const t = e[y] || {};
      const biz = parseInt(t.biz || 0, 10) || 0;
      const karsi = parseInt(t.karsi || 0, 10) || 0;
      const gelmedi = parseInt(t.gelmedi || 0, 10) || 0;
      if (biz + karsi + gelmedi > 0) out[y] = { biz, karsi, gelmedi };
    }
    return out;
  };

  const save = async (no) => {
    setSavingNo(no);
    try {
      await api.patch(`/committees/${no}/elections`, { elections: numify(edits[no]) });
      toast.success(`Komite #${no} geçmiş sonuçları kaydedildi`);
      setCommittees((prev) => prev.map((c) => (c.committee_no === no ? { ...c, election_totals: numify(edits[no]) } : c)));
    } catch {
      toast.error("Kaydedilemedi");
    } finally {
      setSavingNo(null);
    }
  };

  const rows = useMemo(() => {
    let r = committees || [];
    if (q.trim()) {
      const s = q.trim().toLocaleLowerCase("tr");
      r = r.filter((c) => `${c.committee_no} ${c.name}`.toLocaleLowerCase("tr").includes(s));
    }
    if (filter === "empty") r = r.filter((c) => !hasData(edits[c.committee_no] || {}));
    if (filter === "filled") r = r.filter((c) => hasData(edits[c.committee_no] || {}));
    return r;
  }, [committees, q, filter, edits]);

  if (!committees) {
    return (
      <div className="space-y-6" data-testid="election-results-loading">
        <Skeleton className="h-9 w-72" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const filledCount = committees.filter((c) => hasData(edits[c.committee_no] || {})).length;

  return (
    <div className="space-y-6" data-testid="election-results-page">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl grid place-items-center bg-[#0F2C59]/10 text-[#0F2C59] dark:text-sky-300 shrink-0">
          <Landmark className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground">Geçmiş Seçim Sonuçları</h1>
          <p className="text-sm text-muted-foreground">Her komitenin 2018 ve 2022 sonuçlarını girin. Bu veriler tahmin ve Yıllar Arası eğilimi besler. <span className="font-medium text-foreground">{filledCount}/{committees.length}</span> komitede veri var.</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Komite ara..." className="pl-9" data-testid="election-search" />
        </div>
        <div className="flex gap-2">
          {[["all", "Tümü"], ["empty", "Boş"], ["filled", "Girilmiş"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`election-filter-${k}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filter === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="election-table">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <erT.Th sortKey="er-komite" get={(r) => r.name} text className="px-3 py-3 font-medium">Komite</erT.Th>
                {YEARS.map((y) => (
                  <th key={y} className="px-3 py-3 font-medium text-center border-l border-border" colSpan={4}>{y}</th>
                ))}
                <th className="px-3 py-3 font-medium text-center border-l border-border">Kaydet</th>
              </tr>
              <tr className="border-b border-border text-left text-muted-foreground text-xs">
                <th className="px-3 py-2 font-medium"></th>
                {YEARS.map((y) => (
                  <React.Fragment key={y}>
                    <th className="px-2 py-2 font-medium text-center border-l border-border">Bize</th>
                    <th className="px-2 py-2 font-medium text-center">Karşı</th>
                    <th className="px-2 py-2 font-medium text-center">Gelmeyen</th>
                    <th className="px-2 py-2 font-medium text-center">Pay</th>
                  </React.Fragment>
                ))}
                <th className="px-3 py-2 border-l border-border"></th>
              </tr>
            </thead>
            <tbody>
              {erT.applySort(rows).map((c) => {
                const e = edits[c.committee_no] || { "2018": {}, "2022": {} };
                const filled = hasData(e);
                return (
                  <tr key={c.committee_no} className="border-b border-border/60 hover:bg-muted/40" data-testid={`election-row-${c.committee_no}`}>
                    <td className="px-3 py-2 min-w-[220px]">
                      <div className="flex items-center gap-1.5">
                        {filled && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                        <span className="text-muted-foreground">#{c.committee_no}</span> <span className="font-medium">{c.name}</span>
                      </div>
                    </td>
                    {YEARS.map((y) => {
                      const t = { biz: +e[y].biz || 0, karsi: +e[y].karsi || 0 };
                      return (
                        <React.Fragment key={y}>
                          {FIELDS.map(([f], i) => (
                            <td key={f} className={`px-1.5 py-2 ${i === 0 ? "border-l border-border" : ""}`}>
                              <Input
                                value={e[y][f]}
                                onChange={(ev) => setVal(c.committee_no, y, f, ev.target.value)}
                                className="h-8 w-16 text-center tabular-nums px-1"
                                inputMode="numeric"
                                data-testid={`election-input-${c.committee_no}-${y}-${f}`}
                              />
                            </td>
                          ))}
                          <td className="px-2 py-2 text-center text-xs text-muted-foreground tabular-nums">{t.biz + t.karsi ? `%${share(t)}` : "—"}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-3 py-2 text-center border-l border-border">
                      <Button size="sm" variant="outline" onClick={() => save(c.committee_no)} disabled={savingNo === c.committee_no} data-testid={`election-save-${c.committee_no}`}>
                        {savingNo === c.committee_no ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Komite bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      <p className="text-xs text-muted-foreground">Pay = Bize / (Bize + Karşı). Tamamı 0 olan yıllar kaydedilmez. Bir komiteye firma eklendikçe bu geçmiş veriler 2026 tahminini ve Karne'deki Yıllar Arası eğilimi daha isabetli yapar.</p>
    </div>
  );
}
